import { reactive, type Reactive, type Ref, type ShallowRef } from 'vue';
import { createInjection } from 'src/lib/createInjection';
import type { MovieList } from 'src/models/movie';
import type { GIMovieList, GIResource, GIResourceWithID, MapSelectOpts } from 'src/models/geoItem';
import type {
  IExtremeMapRef,
  Location,
  MapElemInfo,
  MovieLayerMeta,
  QueryArea,
  Tab,
} from 'src/models';
import type { LayoutStyles, TopMovieSearch } from 'src/models/top';
import { DEFAULT_DATE_RANGE_DAYS } from 'src/consts/top';
import { useMovieSearchParams, type MovieSearchParamsContext } from 'src/composables/useMovieSearchParams';
import { useMovieSearchExecution, type MovieSearchExecutionContext } from 'src/composables/useMovieSearchExecution';
import { useMovieSearchResultInteraction, type MovieSearchResultInteractionContext } from 'src/composables/useMovieSearchResultInteraction';

// エリア検索のデフォルト半径(m)。実行中に変化しない値のためモジュール定数として定義する。
const AREA_QUERY_RADIUS_METER_DEFAULT = 500;

// 3つの内部composableの公開キー＋facade直付けのキーで構成するstore型。
// isValidMovieSearchDateParams / refreshMovieLayer はcomposable間の内部連携専用のため公開しない。
export type MovieSearchStore =
  Omit<MovieSearchParamsContext, 'isValidMovieSearchDateParams'> &
  Omit<MovieSearchExecutionContext, 'refreshMovieLayer'> &
  MovieSearchResultInteractionContext & {
    movieSearch: Reactive<TopMovieSearch>;
    tabs: Ref<Tab[]>;
    styles: LayoutStyles;
    onTabElementClick: (element: MapElemInfo<GIResourceWithID>) => Promise<void>;
  };

const { provide: provideMovieSearchCtx, inject: injectMovieSearchCtx } =
  createInjection<MovieSearchStore>('useMovieSearch');

export function useMovieSearchStore(): MovieSearchStore {
  return injectMovieSearchCtx();
}

// 動画検索の①パラメータ／②検索実行／③結果一覧操作の3つのcomposableを束ね、
// 従来と同じキー集合を持つstoreを返す薄いファサード。
// provide/injectは親→子方向のみで、provider自身はこのstoreをinjectできないため、
// provider側でも利用できるようprovideするだけでなくstoreを返り値でも渡す。
export function provideMovieSearch(deps: {
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  tabs: Ref<Tab[]>;
  styles: LayoutStyles;
  afterDataLayerItemClick: (dataName: string, obj: GIResource, opts?: MapSelectOpts) => void;
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null, opts?: MapSelectOpts) => void;
  getSelectedElemDataName: () => string | null;
  afterMoviePlayClick: (obj: MovieList | GIMovieList) => void;
  onTabElementClick: (element: MapElemInfo<GIResourceWithID>) => Promise<void>;
}): MovieSearchStore {
  const { movieLayerMeta, refExtremeMap, tabs, styles } = deps;
  const now = new Date();
  const initMovieSearchState = (): TopMovieSearch => {
    return {
      params: {
        // 2週間前から今日
        dtFrom: new Date(now.getFullYear(), now.getMonth(), now.getDate() - DEFAULT_DATE_RANGE_DAYS),
        dtTo: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        roadNameDispObj: null,
        direction: '',
        kpCalcFrom: '',
        kpCalcTo: '',
        carName: '',
        tags: [],
      },
      areaQueryObj: null,
      areaQueryRadiusMeter: AREA_QUERY_RADIUS_METER_DEFAULT,
      isQuerying: false,
      resultMode: 'none',
      results: [],
      showTagModal: false,
    };
  };
  const movieSearchState = reactive<TopMovieSearch>(initMovieSearchState());

  // params⇔execution は相互参照になり得る（onDateParamChange/onTagParamChangeが
  // 範囲検索の再実行のためdoAreaMovieSearchを呼ぶ）ため、循環importを避けて
  // 遅延参照（クロージャ）で解決する。params→executionへは注入するコールバックの中でのみ
  // 参照し、execution→paramsへはバリデーション結果（computed）を直接渡す一方向依存にする。
  const params = useMovieSearchParams({
    movieSearchState,
    doAreaMovieSearch: (queryArea: QueryArea) => execution.doAreaMovieSearch(queryArea),
  });

  const execution = useMovieSearchExecution({
    movieSearchState,
    movieLayerMeta,
    refExtremeMap,
    setMapSelectedElemInfo: deps.setMapSelectedElemInfo,
    getSelectedElemDataName: deps.getSelectedElemDataName,
    isValidMovieSearchParams: params.isValidMovieSearchParams,
    isValidRouteMovieSearchDateParams: params.isValidRouteMovieSearchDateParams,
    isValidMovieSearchDateParams: params.isValidMovieSearchDateParams,
  });

  const resultInteraction = useMovieSearchResultInteraction({
    movieSearchState,
    refreshMovieLayer: execution.refreshMovieLayer,
    afterDataLayerItemClick: deps.afterDataLayerItemClick,
    afterMoviePlayClick: deps.afterMoviePlayClick,
  });

  const store = {
    movieSearch: movieSearchState,
    isMovieSearchResultModeNone: execution.isMovieSearchResultModeNone,
    isMovieSearchResultModeDetail: execution.isMovieSearchResultModeDetail,
    isMovieSearchResultModeArea: execution.isMovieSearchResultModeArea,
    isMovieSearchResultModeRoute: execution.isMovieSearchResultModeRoute,
    isValidMovieSearchParams: params.isValidMovieSearchParams,
    isValidRouteMovieSearchDateParams: params.isValidRouteMovieSearchDateParams,
    searchParamMovieSearchButtonTooltip: params.searchParamMovieSearchButtonTooltip,
    tabs,
    styles,
    onTagParamChange: params.onTagParamChange,
    doSearchParamMovieSearch: execution.doSearchParamMovieSearch,
    doRouteMovieSearch: execution.doRouteMovieSearch,
    doAreaMovieSearch: execution.doAreaMovieSearch,
    onDateParamChange: params.onDateParamChange,
    onRoadNameDispChange: params.onRoadNameDispChange,
    openTagModal: params.openTagModal,
    onMovieSearchResultRowClick: resultInteraction.onMovieSearchResultRowClick,
    onMovieSearchResultPlayClick: resultInteraction.onMovieSearchResultPlayClick,
    onTabElementClick: deps.onTabElementClick,
    setMovieQueryStart: execution.setMovieQueryStart,
    setMovieQueryDone: execution.setMovieQueryDone,
  };
  provideMovieSearchCtx(store);
  return store;
}
